import { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router-dom';

type Size = 'sm' | 'md' | 'lg';
type Tone = 'default' | 'danger';

const SIZE: Record<Size, string> = {
  // Design `.ab` row action.
  sm: 'h-[34px] w-[34px] rounded-[10px]',
  // Design `.ibtn` small (callout dismiss).
  md: 'h-9 w-9 rounded-[10px]',
  // Design `.ibtn` top-bar button.
  lg: 'h-11 w-11 rounded-[12px]',
};

const TONE: Record<Tone, string> = {
  default: 'text-muted-2 hover:bg-note hover:text-ink',
  // Design `.ab.del`: red only on hover.
  danger: 'text-muted-2 hover:border-danger-line hover:bg-danger-bg hover:text-danger-ink',
};

/** Classes for a link or custom element that must look like an IconButton. */
export function iconButtonClass(size: Size = 'sm', tone: Tone = 'default', className = '') {
  return `inline-grid flex-none place-items-center border border-line-strong bg-surface p-0 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:pointer-events-none disabled:opacity-40 ${SIZE[size]} ${TONE[tone]} ${className}`;
}

interface Props extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Accessible name (also the tooltip title unless `title` is given). */
  label: string;
  icon: ReactNode;
  size?: Size;
  tone?: Tone;
  /** Render as a router link instead of a button. */
  to?: string;
}

/** Square bordered icon-only control: `.ab` (34px, r10) or `.ibtn` (44px, r12). */
export default function IconButton({ label, icon, size = 'sm', tone = 'default', to, className = '', type = 'button', title, ...rest }: Props) {
  const cls = iconButtonClass(size, tone, className);
  if (to) return <Link to={to} aria-label={label} title={title ?? label} className={cls}>{icon}</Link>;
  return <button type={type} aria-label={label} title={title ?? label} {...rest} className={cls}>{icon}</button>;
}
